import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DragDropContext } from '@hello-pangea/dnd';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { confirm, showAlert } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import useFetchWithStatus from 'hooks/fetchWithStatus';
import groupBy from 'lodash/groupBy';
import API from 'services/API';
import strings from 'strings';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { defaultValidExtNames } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { formatBytes, getErrorMessage } from 'UI/utils';

import { CategoryCard } from './CategoryCard';
import {
  hasReachedMaxFiles,
  moveFileIntoSelectedCategory,
  updateCategoryByEndpoint
} from './fileManager.utils';
import { useStyles } from './styles';

/**
 * @param {Object} props
 * @param {string[]} [props.allowedExtensions]
 * @param {boolean} [props.allowMoveFiles=true]
 * @param {number} [props.apiVersion=1]
 * @param {number} [props.apiVersionForDeleting=2]
 * @param {Object[]} [props.defaultCategories=null]
 * @param {Object} [props.FileDraggableProps={}]
 * @param {string} [props.fileNameField='file_name']
 * @param {string} props.filesEndpoint
 * @param {Object[]} [props.initialFiles=[]]
 * @param {boolean} [props.layoutInColumns=false]
 * @param {string} props.module
 * @param {Function} [props.onAttachmentsChanged]
 * @param {Function} [props.onReset]
 * @param {boolean} [props.readOnly=false]
 * @param {boolean} [props.shouldReset]
 * @param {boolean} [props.showFileSize=false]
 * @param {boolean} [props.showItemDate=false]
 * @param {boolean} [props.showItemMenu=false]
 * @param {boolean} [props.viewItemOnClick=false]
 */
const FileManager = ({
  allowedExtensions = defaultValidExtNames,
  allowMoveFiles = true,
  apiVersion = 1,
  apiVersionForDeleting = 2,
  defaultCategories = null,
  FileDraggableProps = {},
  fileNameField = 'file_name',
  filesEndpoint,
  initialFiles = [],
  layoutInColumns = false,
  module,
  onAttachmentsChanged = () => {},
  onReset = () => {},
  readOnly = false,
  shouldReset = false,
  showFileSize = false,
  showItemDate = false,
  showItemMenu = false,
  viewItemOnClick = false
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [attachments, setAttachments] = useState([]);
  const [categorySelected, setCategorySelected] = useState(null);
  const [categorySource, setCategorySource] = useState(null);
  const [fileSelected, setFileSelected] = useState(undefined);
  const [uiState, setUiState] = useState({
    isFetching: false,
    isOpen: false
  });

  const { state: categoryResponse } = useFetchWithStatus(
    `${Endpoints.FileCategories}?module=${module}&sort=order:asc`
  );

  const { state: attachmentResponse } = useFetchWithStatus(
    !defaultCategories ? `${filesEndpoint}?direction=desc&includes=fileType,vCreatedBy` : null,
    initialFiles,
    UIStatus.Default,
    apiVersion
  );

  useEffect(() => {
    if (shouldReset) {
      const emptyCategories = attachments.map(category => ({ ...category, files: [] }));
      setAttachments(emptyCategories);
      onReset && onReset();
    }
  }, [attachments, shouldReset, onReset]);

  useEffect(() => {
    setUiState(prev => ({ ...prev, isFetching: true }));
    if (categoryResponse.status === UIStatus.Error || attachmentResponse.status === UIStatus.Error)
      return;
    if (
      categoryResponse.status === UIStatus.Loading ||
      attachmentResponse.status === UIStatus.Loading
    )
      return;

    const categoryResp = defaultCategories
      ? categoryResponse.results.filter(category =>
          defaultCategories.find(({ id }) => category.id === id)
        )
      : categoryResponse.results;

    const categories = categoryResp.map(
      ({ id, description, max_files: maxFiles, multiple, required, title }) => ({
        description,
        files: [],
        id,
        maxFiles,
        multiple,
        required,
        title
      })
    );

    const files = attachmentResponse.results.map(file => ({
      ...file,
      isLoading: false,
      message: '',
      type: 'existing'
    }));

    const filesByCategory = groupBy(files, 'fileType.id');

    const newAttachments = categories.map(category => ({
      ...category,
      files: filesByCategory[category.id] || []
    }));

    setAttachments(newAttachments);
    setUiState(prev => ({ ...prev, isFetching: false }));
  }, [attachmentResponse, categoryResponse, defaultCategories]);

  useEffect(() => {
    onAttachmentsChanged && onAttachmentsChanged(attachments);
  }, [attachments, onAttachmentsChanged]);

  const showError = error => {
    dispatch(
      showAlert({
        severity: 'error',
        title: error?.response?.data?.title ?? strings.fileManager.error.title,
        body: getErrorMessage(error, strings.shared.errors.serverError.subtitle)
      })
    );
  };

  const updateFileLoadingStatus = ({ fileId, loading, message = '' }) => {
    setAttachments(prev =>
      prev.map(category => ({
        ...category,
        files: category.files.map(file =>
          file.id === fileId ? { ...file, isLoading: loading, message } : file
        )
      }))
    );
  };

  const handleDragEnd = async result => {
    if (
      !result.destination ||
      result.source.droppableId === result.destination.droppableId ||
      readOnly
    )
      return;

    const { draggableId, destination, source } = result;
    const fileId = parseInt(draggableId, 10);
    const sourceId = parseInt(source.droppableId, 10);
    const sourceIndex = source.index;
    const destinationId = parseInt(destination.droppableId, 10);
    const destinationIndex = destination.index;

    if (hasReachedMaxFiles({ attachments, destinationId })) {
      dispatch(
        showAlert({
          severity: 'warning',
          title: strings.fileManager.validQuantity.title,
          body: strings.fileManager.validQuantity.message
        })
      );
      return;
    }

    const previousAttachments = attachments;
    const filesMoved = moveFileIntoSelectedCategory({
      attachments,
      fileId,
      sourceId,
      sourceIndex,
      destinationId,
      destinationIndex
    });
    setAttachments(filesMoved);

    updateCategoryByEndpoint({ endpoint: filesEndpoint, fileId, newCategoryId: destinationId })
      .then(() => {
        updateFileLoadingStatus({ fileId, loading: false });
      })
      .catch(error => {
        showError(error);
        setAttachments(previousAttachments);
        updateFileLoadingStatus({ fileId, loading: false });
      });
  };

  const handleFileMove = category => async file => {
    if (!category || !file) return;

    setCategorySource(category);
    setFileSelected(file);
    setUiState(prev => ({ ...prev, isOpen: true }));
  };

  const handleSelect = (name, value) => setCategorySelected(value);

  const handleConfirm = () => {
    const result = {
      destination: {
        droppableId: categorySelected.id.toString(),
        index: 0
      },
      draggableId: fileSelected.id.toString(),

      source: {
        index: categorySource.files.findIndex(item => item.id === fileSelected.id),
        droppableId: categorySource.id.toString()
      }
    };
    handleCloseConfirm();

    handleDragEnd(result);
  };

  const handleCloseConfirm = () => {
    setUiState(prev => ({ ...prev, isOpen: false }));
    setCategorySelected(null);
    setCategorySource(null);
    setFileSelected(null);
  };

  const handleFileDelete = categorySourceId => async file => {
    if (!file) return;

    const fileName = file[fileNameField] || file.original_name || file.file_name;
    dispatch(
      confirm({
        severity: 'warning',
        title: strings.fileManager.deleteFile.title,
        message: strings.formatString(strings.fileManager.deleteFile.message, {
          fileName
        }),
        onConfirm: async ok => {
          try {
            if (!ok) return;

            updateFileLoadingStatus({
              fileId: file.id,
              loading: true,
              message: 'Deleting file...'
            });

            await deleteRemoteFile(file, categorySourceId);
          } catch (err) {
            showError(err);
          }
        }
      })
    );
  };

  const removeAttachment = ({ attachmentId, categorySourceId }) => {
    setAttachments(prev =>
      prev.map(category =>
        category.id === categorySourceId
          ? {
              ...category,
              files: category.files.filter(file => file.id !== attachmentId)
            }
          : category
      )
    );
  };

  const deleteRemoteFile = async (attachment, categorySourceId) => {
    try {
      if (attachment?.id === undefined || attachment?.id === null) return;

      const finalEndpoint = `${filesEndpoint}/${attachment.id}`;
      const response = await API.delete(finalEndpoint, { apiVersion: apiVersionForDeleting });
      if (response.status === HTTPStatusCodes.Ok) {
        dispatch(
          showAlert({
            severity: 'success',
            title: strings.fileManager.deleteFile.successTitle,
            body: strings.formatString(strings.fileManager.deleteFile.successMessage, {
              fileName: attachment[fileNameField]
            })
          })
        );

        removeAttachment({ attachmentId: attachment.id, categorySourceId });
      }
    } catch (err) {
      if (err?.response?.status === HTTPStatusCodes.NotFound) {
        removeAttachment({ attachmentId: attachment.id, categorySourceId });
        return;
      }

      showError(err);
      updateFileLoadingStatus({
        fileId: attachment.id,
        loading: false
      });
    }
  };

  const handleUploadFiles = async (files, categoryId) =>
    Promise.all(await files.map(async file => uploadFile(file, categoryId)));

  const uploadFile = async (file, categoryId) => {
    const data = new FormData();
    data.append('file', file);
    data.append('file_type_id', categoryId);

    const fileInfo = {
      id: 0,
      [fileNameField]: file.name,
      isLoading: true,
      size: file.size,
      message: strings.formatString(strings.fileManager.dropzone.uploading, {
        fileSize: formatBytes(file.size)
      }),
      fileType: {
        id: categoryId
      }
    };

    setAttachments(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              files: [...category.files, fileInfo]
            }
          : category
      )
    );

    try {
      const uploadResponse = await API.post(filesEndpoint, data);

      uploadResponse.status === 201 && processUploadResponse(uploadResponse, categoryId);
    } catch (error) {
      showError(error);
      setAttachments(prev =>
        prev.map(category =>
          category.id === categoryId
            ? {
                ...category,
                files: category.files.filter(att => att[fileNameField] !== fileInfo[fileNameField])
              }
            : category
        )
      );
    }
  };

  const processUploadResponse = (uploadResponse, categoryId) => {
    const { data } = uploadResponse;

    setAttachments(prev =>
      prev.map(category =>
        category.id === categoryId
          ? {
              ...category,
              files: category.files.map(file =>
                file[fileNameField] === data[fileNameField]
                  ? {
                      ...data,
                      isLoading: false,
                      message: '',
                      fileType: {
                        id: categoryId
                      }
                    }
                  : file
              )
            }
          : category
      )
    );
  };

  if (uiState.isFetching) {
    return (
      <div className={classes.emptyStateContainer}>
        <EmptyPlaceholder {...strings.shared.loading} />
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {!readOnly && (
        <div className={classes.marginBottom}>
          <Typography variant="body1">* {strings.fileManager.fileName.message}</Typography>
        </div>
      )}
      <div className={layoutInColumns ? classes.columnContainer : classes.rowContainer}>
        <DragDropContext onDragEnd={handleDragEnd}>
          {attachments.map(category => (
            <CategoryCard
              key={category.id}
              attachments={attachments}
              allowedExtensions={allowedExtensions}
              category={category}
              FileDraggableProps={FileDraggableProps}
              fileNameField={fileNameField}
              layoutInColumns={layoutInColumns}
              onFileDelete={handleFileDelete(category.id)}
              onFileMove={allowMoveFiles ? handleFileMove(category) : undefined}
              onUploadFiles={handleUploadFiles}
              readOnly={readOnly}
              showFileSize={showFileSize}
              showItemDate={showItemDate}
              showItemMenu={showItemMenu}
              viewItemOnClick={viewItemOnClick}
            />
          ))}
        </DragDropContext>
      </div>

      {uiState.isOpen && (
        <DecisionDialog
          isOpened={uiState.isOpen}
          title={strings.fileManager.moveFileConfirm.title}
          message={strings.formatString(strings.fileManager.moveFileConfirm.message, {
            fileName: fileSelected[fileNameField]
          })}
          withButtons="Confirmation"
          confirmButtonText="Move File"
          showSeverityIcon={false}
          onConfirm={handleConfirm}
          severity="info"
          disableConfirmButton={!categorySelected}
          onClose={handleCloseConfirm}
          onError={showError}
        >
          <AutocompleteSelect
            name="category"
            placeholder="Select section"
            options={attachments}
            selectedValue={categorySelected}
            onSelect={handleSelect}
            width="90%"
          />
        </DecisionDialog>
      )}
    </>
  );
};

export default FileManager;
